/**
 * The right-hand panel: the process, a box, or an arrow.
 *
 * Three things this rewrite is about.
 *
 * **It is readable.** Every field sits on `--color-input-bg` inside a panel on
 * `--color-bg-secondary`. The first version put fields on `--color-bg`, which
 * is the page ground (`#f4f6f9` in the light theme) — grey text on grey.
 *
 * **A next step can be fully specified here.** Trigger, condition, assignment,
 * due date, priority, the verb, what clicking it opens, the pre-fill, and
 * whether it raises a worklist task at all. The arrow used to compile a rule
 * nobody could see the whole of, in a designer that could only author a third
 * of it.
 *
 * **The record match is a pick, not a memory test.** Both objects' real DB
 * columns are offered, keys first, so a match can't quietly join on a column
 * that does not exist.
 */

import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { FlaskConical, Trash2, ArrowLeftRight, Plus, X } from 'lucide-react'

import type {
  FlowDoc, FlowEdge, FlowNode, FlowPalette, MatchColumn, NodeKind,
} from '@/api/processflow'
import { livePreviewExecute } from '@/livepreview/api'
import { AUTHORED_EDGE_KINDS, EDGE_STYLE, KIND_STYLE } from './shapes'

interface Props {
  project: string
  flow: FlowDoc
  palette: FlowPalette | null
  selection: { kind: 'node' | 'edge'; id: string } | null
  titles: Record<string, string>
  onFlowChange: (patch: Partial<FlowDoc>) => void
  onNodeChange: (id: string, patch: Partial<FlowNode>) => void
  onEdgeChange: (id: string, patch: Partial<FlowEdge>) => void
  onDeleteNode: (id: string) => void
  onDeleteEdge: (id: string) => void
  onDeleteFlow: () => void
  canDeleteFlow: boolean
}

export default function FlowInspector(props: Props) {
  const { flow, selection } = props
  const node = selection?.kind === 'node'
    ? (flow.nodes || []).find((n) => n.id === selection.id) : null
  const edge = selection?.kind === 'edge'
    ? (flow.edges || []).find((e) => e.id === selection.id) : null

  return (
    <div style={{
      width: 340, flexShrink: 0, overflowY: 'auto', padding: '12px 13px 28px',
      borderLeft: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
    }}>
      {node && <NodePanel {...props} node={node} />}
      {edge && <EdgePanel {...props} edge={edge} />}
      {!node && !edge && <FlowPanel {...props} />}
    </div>
  )
}

// ── the process itself ─────────────────────────────────────────────────────

function FlowPanel({ flow, onFlowChange, onDeleteFlow, canDeleteFlow }: Props) {
  return (
    <>
      <Head>This process</Head>
      <Field l="Name" h="The file this process is saved as. Renaming it moves the rules it manages with it.">
        <input style={field} value={flow.name}
          onChange={(e) => onFlowChange({ name: e.target.value })} />
      </Field>
      <Field l="Title" h="What people see in the process picker.">
        <input style={field} value={flow.title}
          onChange={(e) => onFlowChange({ title: e.target.value })} />
      </Field>
      <Field l="Description">
        <textarea style={{ ...field, minHeight: 62 }} value={flow.description}
          onChange={(e) => onFlowChange({ description: e.target.value })} />
      </Field>

      <Note>
        <strong>Drawing it.</strong> Place a step, an entity or a data store from
        the left. Select a box and drag its blue handle onto another — or press
        <em> Connect boxes</em> on the canvas and click the two in turn. Click an
        arrow to say what it means and who does it.
      </Note>

      {canDeleteFlow && (
        <button type="button" style={{ ...ghost, marginTop: 14, color: 'var(--color-error, #b91c1c)' }}
          onClick={onDeleteFlow}>
          <Trash2 size={13} /> Delete this process
        </button>
      )}
    </>
  )
}

// ── one box ────────────────────────────────────────────────────────────────

function NodePanel({
  node, palette, titles, onNodeChange, onDeleteNode,
}: Props & { node: FlowNode }) {
  const kind = (node.kind || 'step') as NodeKind
  const st = KIND_STYLE[kind]
  const objects = palette?.objects || []
  const known = [...objects, ...(palette?.extras || [])]
  const inNavigation = known.find((o) => o.obj_name === node.obj_name)?.in_navigation !== false

  return (
    <>
      <Head
        right={
          <button type="button" style={iconBtn} title="Remove from this diagram"
            onClick={() => onDeleteNode(node.id)}>
            <Trash2 size={13} />
          </button>
        }
      >
        <span style={{ color: st.stroke }}>{st.label}</span>
        {' · '}
        {titles[node.obj_name] || node.label || node.obj_name}
      </Head>

      <Field l="Shown as" h="The name on the box. Blank uses whatever the application calls it.">
        <input style={field} value={node.label || ''}
          onChange={(e) => onNodeChange(node.id, { label: e.target.value })} />
      </Field>

      <Field l="This box is" h={st.hint}>
        <select style={field} value={kind}
          onChange={(e) => onNodeChange(node.id, { kind: e.target.value as NodeKind })}>
          {(Object.keys(KIND_STYLE) as NodeKind[]).map((k) => (
            <option key={k} value={k}>{KIND_STYLE[k].label}</option>
          ))}
        </select>
      </Field>

      {kind === 'store' && (
        <Note>
          A data store is an ordinary object the process <strong>writes</strong> —
          stock, a ledger, a balance — that nobody types into. Marking it one
          stops an arrow into it raising a task, and draws it as the drum so
          anybody reading the diagram can see where the data lands.
          {!inNavigation && (
            <>
              {' '}It has no menu entry, so nobody can open it to see what
              changed — worth adding to the navigation.
            </>
          )}
        </Note>
      )}

      {kind === 'process' && (
        <>
          <Field l="Process handed to">
            <select style={field} value={node.flow || ''}
              onChange={(e) => onNodeChange(node.id, {
                flow: e.target.value, obj_name: e.target.value,
              })}>
              <option value="">Choose a process…</option>
              {(palette?.flows || []).map((f) => (
                <option key={f.name} value={f.name}>{f.title}</option>
              ))}
            </select>
          </Field>
          <Field l="Starts at" h="The screen a task handed over here should open. Blank uses that process's own first step.">
            <select style={field} value={node.entry_obj || ''}
              onChange={(e) => onNodeChange(node.id, { entry_obj: e.target.value })}>
              <option value="">Its first step</option>
              {objects.map((o) => (
                <option key={o.obj_name} value={o.obj_name}>{o.label}</option>
              ))}
            </select>
          </Field>
        </>
      )}

      {kind === 'external' && (
        <Field l="Who" h="A party outside the application — a customer, a bank, a courier.">
          <input style={field} value={node.obj_name || ''}
            onChange={(e) => onNodeChange(node.id, { obj_name: e.target.value })} />
        </Field>
      )}

      <Field l="Note" h="For whoever reads this diagram next. It is not shown at run time.">
        <textarea style={{ ...field, minHeight: 54 }} value={node.note || ''}
          onChange={(e) => onNodeChange(node.id, { note: e.target.value })} />
      </Field>

      {(kind === 'step' || kind === 'entity') && (
        <Note>
          Object <code>{node.obj_name}</code>
          {kind === 'entity'
            ? ' — an entity sits beside the process rather than in it. Records still open from a trail, but nobody is asked to work through it.'
            : ' — records of this object are what a process trail walks.'}
        </Note>
      )}
    </>
  )
}

// ── one arrow ──────────────────────────────────────────────────────────────

function EdgePanel({
  project, flow, edge, palette, titles, onEdgeChange, onDeleteEdge,
}: Props & { edge: FlowEdge }) {
  const [testing, setTesting] = useState(false)
  const nodes = flow.nodes || []
  const src = nodes.find((n) => n.id === edge.from)
  const dst = nodes.find((n) => n.id === edge.to)
  const name = (n?: FlowNode) => (n ? titles[n.obj_name] || n.label || n.obj_name : '?')
  const st = EDGE_STYLE[edge.kind] || EDGE_STYLE.work
  const rule = edge.rule || {}
  const targetIsRecord = ['step', 'entity', 'store'].includes(dst?.kind || 'step')
  const allowedKinds = AUTHORED_EDGE_KINDS.filter((k) => {
    // Nobody types into a store, so a next step cannot point at one.
    if (dst?.kind === 'store') return k !== 'work'
    if (dst?.kind === 'external') return k !== 'work'
    if (dst?.kind === 'process') return k === 'work' || k === 'link'
    return k === 'work' || k === 'link' || k === 'read'
  })

  const srcCols = palette?.columns?.[src?.obj_name || ''] || []
  const dstCols = palette?.columns?.[dst?.obj_name || ''] || []

  const patchRule = (patch: Record<string, unknown>) =>
    onEdgeChange(edge.id, { rule: { ...rule, ...patch } })
  const patchTarget = (patch: Record<string, unknown>) =>
    patchRule({ target: { ...(rule.target || {}), ...patch } })
  const patchAssign = (patch: Record<string, unknown>) =>
    patchRule({ assign: { ...(rule.assign || {}), ...patch } })

  /**
   * Run the condition against live rows before it ships. A condition broader
   * than its author intended does not fail — it quietly raises a task per
   * matching record, which on a real table can be thousands.
   */
  const test = async () => {
    if (!src) return
    setTesting(true)
    try {
      const res = await livePreviewExecute(project, src.obj_name, 'worklist_test_rule', {
        obj_name: src.obj_name,
        rule: {
          ...rule,
          target: { ...(rule.target || {}), obj_name: dst?.obj_name || '' },
        },
      }) as { matched?: number; assignees?: string[]; error?: string }
      if (res?.error) { toast.error(res.error); return }
      const n = res?.matched ?? 0
      const who = (res?.assignees || []).join(', ')
      toast.success(n === 0
        ? 'No records match right now — nobody would be asked to do anything.'
        : `${n} record${n === 1 ? '' : 's'} match${n === 1 ? 'es' : ''}${who ? `, waiting on ${who}` : ''}.`,
      { duration: 6000 })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not test that arrow.')
    } finally { setTesting(false) }
  }

  return (
    <>
      <Head
        right={
          <>
            <button type="button" style={iconBtn} title="Reverse this arrow"
              onClick={() => onEdgeChange(edge.id, {
                from: edge.to, to: edge.from,
                match: (edge.match || []).map((m) => ({ from: m.to, to: m.from })),
              })}>
              <ArrowLeftRight size={13} />
            </button>
            <button type="button" style={iconBtn} title="Delete this arrow"
              onClick={() => onDeleteEdge(edge.id)}>
              <Trash2 size={13} />
            </button>
          </>
        }
      >
        <span style={{ color: st.stroke }}>{name(src)} → {name(dst)}</span>
      </Head>

      <Field l="This arrow means" h={st.hint}>
        <select style={field} value={edge.kind}
          onChange={(e) => onEdgeChange(edge.id, { kind: e.target.value })}>
          {allowedKinds.map((k) => (
            <option key={k} value={k}>{EDGE_STYLE[k].label} — {EDGE_STYLE[k].hint}</option>
          ))}
        </select>
      </Field>

      <Field l="Label on the arrow" h="Optional. What this transition is called, e.g. “qualified”.">
        <input style={field} value={edge.label || ''}
          onChange={(e) => onEdgeChange(edge.id, { label: e.target.value })} />
      </Field>

      {(edge.kind === 'work' || edge.kind === 'link') && targetIsRecord && (
        <MatchEditor
          edge={edge} srcCols={srcCols} dstCols={dstCols}
          srcName={name(src)} dstName={name(dst)}
          onChange={(match) => onEdgeChange(edge.id, { match })}
        />
      )}

      {(edge.kind === 'update' || edge.kind === 'read') && (
        <Note>
          {edge.kind === 'update'
            ? `${name(src)} writes to ${name(dst)}. Nothing is compiled from this arrow — it is the part of the process that happens without anybody being asked, which is exactly what a data store is for.`
            : `${name(src)} looks ${name(dst)} up. Informational: it records the dependency so the diagram tells the truth about what this step needs.`}
        </Note>
      )}

      {edge.kind === 'work' && (
        <>
          <Divider>The task it raises</Divider>

          <Toggle
            on={edge.raises_task !== false}
            onChange={(on) => onEdgeChange(edge.id, { raises_task: on })}
            label="Raises a task in people's worklists"
            hint={edge.raises_task === false
              ? 'Off — this arrow says what follows what, and asks nobody to do anything. Nothing is compiled.'
              : 'On — while the condition below holds, this shows up in the Work panel of everyone it is assigned to.'}
          />

          {edge.raises_task !== false && (
            <>
              <Field l="Rule name" h="Part of the task identity, unique per source object. Changing it closes the old tasks and opens new ones.">
                <input style={field} value={rule.name || ''}
                  onChange={(e) => patchRule({ name: e.target.value })} />
              </Field>
              <Field l="Task title" h="What the person reads. Use {form.Column} to fill in the record's own values.">
                <input style={field} value={rule.title || ''}
                  placeholder="Receive goods against {form.PoNo}"
                  onChange={(e) => patchRule({ title: e.target.value })} />
              </Field>
              <Field l="Second line" h="Optional, same syntax.">
                <input style={field} value={rule.summary || ''}
                  onChange={(e) => patchRule({ summary: e.target.value })} />
              </Field>

              <Field l="When" h="A state rule waits for a condition; a reminder fires relative to a date; an event rule is raised by business logic.">
                <select style={field} value={rule.trigger?.kind || 'state'}
                  onChange={(e) => patchRule({
                    trigger: { ...(rule.trigger || {}), kind: e.target.value },
                  })}>
                  <option value="state">While a condition is true</option>
                  <option value="temporal">Before or after a date</option>
                  <option value="event">When business logic raises it</option>
                </select>
              </Field>

              {rule.trigger?.kind === 'temporal' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Field l="Date column">
                    <ColumnSelect cols={srcCols} value={rule.trigger?.anchor || ''}
                      onChange={(v) => patchRule({ trigger: { ...(rule.trigger || {}), anchor: v } })} />
                  </Field>
                  <Field l="Days offset" h="Negative is before.">
                    <input style={field} type="number" value={rule.trigger?.offset_days ?? 0}
                      onChange={(e) => patchRule({
                        trigger: { ...(rule.trigger || {}), offset_days: Number(e.target.value) },
                      })} />
                  </Field>
                </div>
              )}

              <Field l="Applies when" h='The same language as a validation, e.g. {form.status} == "QUALIFIED". Blank means always.'>
                <input style={{ ...field, fontFamily: 'monospace', fontSize: '0.8225rem' }}
                  value={rule.condition || ''}
                  onChange={(e) => patchRule({ condition: e.target.value })} />
              </Field>

              <Field l="Assign to roles" h="Comma separated. Everyone in the role sees it; one person acting closes it for all.">
                <ParsedList
                  key={`roles:${edge.id}`}
                  list="pf-roles"
                  text={(rule.assign?.roles || []).join(', ')}
                  onText={(typed) => patchAssign({
                    roles: typed.split(',').map((s) => s.trim()).filter(Boolean),
                  })} />
                <datalist id="pf-roles">
                  {(palette?.roles || []).map((r) => <option key={r} value={r} />)}
                </datalist>
              </Field>
              <Field l="…and the person named in" h="A column holding a user, such as the record's owner.">
                <ColumnSelect
                  cols={srcCols} multi
                  value={(rule.assign?.user_columns || []).join(',')}
                  onChange={(v) => patchAssign({
                    user_columns: v.split(',').map((s) => s.trim()).filter(Boolean),
                  })} />
              </Field>
              {!(rule.assign?.roles || []).length && !(rule.assign?.user_columns || []).length && (
                <Warn>A task assigned to nobody is invisible. Name a role or a column.</Warn>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Field l="Due date from" h="Drives the overdue flag.">
                  <ColumnSelect cols={srcCols} value={rule.due?.column || ''}
                    onChange={(v) => patchRule({ due: { ...(rule.due || {}), column: v } })} />
                </Field>
                <Field l="Priority">
                  <select style={field} value={rule.priority || 'normal'}
                    onChange={(e) => patchRule({ priority: e.target.value })}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </Field>
              </div>

              <Divider>What the person clicks</Divider>

              <Field l="Button says" h="The verb on the worklist row.">
                <input style={field} value={rule.target?.verb || ''}
                  placeholder="Receive goods"
                  onChange={(e) => patchTarget({ verb: e.target.value })} />
              </Field>
              <Field l="Clicking it opens" h="A next step normally creates the record on the object this arrow points at.">
                <select style={field} value={rule.target?.open || 'create'}
                  onChange={(e) => patchTarget({ open: e.target.value })}>
                  <option value="create">Create a record on {name(dst)}</option>
                  <option value="record">Open this record</option>
                  <option value="contact">Contact someone (writes nothing)</option>
                  <option value="checklist">Checklist / confirm</option>
                  <option value="url">Open a link</option>
                </select>
              </Field>
              {(rule.target?.open || 'create') === 'create' && (
                <SeedEditor
                  seed={rule.target?.seed || {}}
                  srcCols={srcCols} dstCols={dstCols}
                  onChange={(seed) => patchTarget({ seed })}
                />
              )}

              <Field l="Stay quiet while" h="Comma separated providers whose open task on the same record silences this one. Default: approval — don't nag about a record that is mid sign-off.">
                <ParsedList
                  key={`quiet:${edge.id}`}
                  text={(rule.suppress_when || ['approval']).join(', ')}
                  onText={(typed) => patchRule({
                    suppress_when: typed.split(',').map((s) => s.trim()).filter(Boolean),
                  })} />
              </Field>

              <button type="button" style={{ ...ghost, marginTop: 4 }} disabled={testing || !src}
                onClick={() => { void test() }}>
                <FlaskConical size={13} /> {testing ? 'Testing…' : 'Test against live records'}
              </button>

              <Note>
                On save this becomes a work rule on <strong>{name(src)}</strong>,
                marked as managed by this process. Rules written by hand on that
                object are left alone.
              </Note>
            </>
          )}
        </>
      )}
    </>
  )
}

// ── the record match ───────────────────────────────────────────────────────

/**
 * Which columns join the two RECORDS.
 *
 * An arrow says a receipt follows an order; it does not say which column of the
 * receipt holds the order's number, and without that the runtime draws the
 * diagram but cannot trace one record through it. This used to be a free-text
 * box — a column typed from memory joins on nothing and fails silently, so both
 * sides are picked from the objects' real columns instead.
 */
function MatchEditor({ edge, srcCols, dstCols, srcName, dstName, onChange }: {
  edge: FlowEdge
  srcCols: MatchColumn[]
  dstCols: MatchColumn[]
  srcName: string
  dstName: string
  onChange: (m: Array<{ from: string; to: string }>) => void
}) {
  const pairs = edge.match || []
  const set = (i: number, side: 'from' | 'to', v: string) => {
    const next = pairs.map((p, j) => (j === i ? { ...p, [side]: v.toUpperCase() } : p))
    onChange(next)
  }
  /** Both objects have a column of this name — the pair somebody almost always
   *  wants, offered as one click instead of two pickers. */
  const shared = useMemo(() => {
    const d = new Set(dstCols.map((c) => c.name))
    return srcCols.filter((c) => d.has(c.name)
      && !pairs.some((p) => p.from === c.name && p.to === c.name))
  }, [srcCols, dstCols, pairs])

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>Match records on</label>
      <p style={hint}>
        Which column of {dstName} holds {srcName}&apos;s value. Leave it empty and
        it is worked out on save — from the pre-fill, then a lookup, then the key.
      </p>

      {pairs.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 4 }}>
          <ColumnSelect cols={srcCols} value={p.from} onChange={(v) => set(i, 'from', v)} />
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>=</span>
          <ColumnSelect cols={dstCols} value={p.to} onChange={(v) => set(i, 'to', v)} />
          <button type="button" style={iconBtn} aria-label="Remove"
            onClick={() => onChange(pairs.filter((_, j) => j !== i))}>
            <X size={12} />
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
        <button type="button" style={miniBtn}
          onClick={() => onChange([...pairs, { from: '', to: '' }])}>
          <Plus size={11} /> Add a pair
        </button>
        {shared.slice(0, 3).map((c) => (
          <button key={c.name} type="button" style={miniBtn}
            title={`${c.label} — both objects have this column`}
            onClick={() => onChange([...pairs, { from: c.name, to: c.name }])}>
            {c.key ? '🔑 ' : ''}{c.name}
          </button>
        ))}
      </div>

      {pairs.length === 0 && (
        <Warn>
          Nothing joins these two records yet. The arrow draws, but a process
          trail steps straight over it.
        </Warn>
      )}
      {pairs.some((p) => !p.from || !p.to) && (
        <Warn>A pair needs a column on both sides — half a match joins on nothing.</Warn>
      )}
    </div>
  )
}

/**
 * A comma list that can actually be typed into.
 *
 * The two list fields above are controlled inputs whose `value` was rendered
 * back OUT of the array their `onChange` had just parsed — and `trim()` +
 * `filter(Boolean)` throw away exactly the characters that BEGIN a second
 * entry. Type the "," that starts a second role and it parses to the same one
 * role, re-renders without the comma, and the list can never grow past one.
 *
 * So the box holds what was TYPED and the array is only ever written outward.
 * The parsing is unchanged, so what a flow SAVES is what it always saved. It
 * resets by being REMOUNTED — key it on the arrow — which is how it goes back
 * to showing the stored value when another arrow is selected, with no draft
 * bookkeeping to get wrong.
 */
function ParsedList({ text, onText, list }: {
  /** The stored array, rendered to text. Shown until the author types. */
  text: string
  /** Called with the raw text on every keystroke; parse it there. */
  onText: (typed: string) => void
  list?: string
}) {
  // null = "not typed in since this box appeared", the one state in which the
  // stored value may overwrite what is on screen.
  const [typed, setTyped] = useState<string | null>(null)
  return (
    <input
      style={field}
      list={list}
      value={typed ?? text}
      onChange={(e) => { setTyped(e.target.value); onText(e.target.value) }}
    />
  )
}

function ColumnSelect({ cols, value, onChange, multi }: {
  cols: MatchColumn[]
  value: string
  onChange: (v: string) => void
  multi?: boolean
}) {
  // With no model to read (an object outside this project, or one not saved
  // yet) the picker degrades to typing rather than to nothing.
  if (!cols.length || multi) {
    return (
      <input style={{ ...field, fontFamily: 'monospace', fontSize: '0.8125rem' }}
        value={value} placeholder={multi ? 'owner, buyer_id' : 'COLUMN'}
        onChange={(e) => onChange(e.target.value)} />
    )
  }
  return (
    <select style={{ ...field, fontSize: '0.8125rem' }} value={value}
      onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a column…</option>
      {cols.map((c) => (
        <option key={`${c.form_no}:${c.name}`} value={c.name}>
          {c.key ? '🔑 ' : ''}{c.name} — {c.label}
        </option>
      ))}
    </select>
  )
}

/** The pre-fill: what the next record already knows when it opens. */
function SeedEditor({ seed, srcCols, dstCols, onChange }: {
  seed: Record<string, string>
  srcCols: MatchColumn[]
  dstCols: MatchColumn[]
  onChange: (s: Record<string, string>) => void
}) {
  const rows = Object.entries(seed)
  const setRow = (i: number, k: string, v: string) => {
    const next: Record<string, string> = {}
    rows.forEach(([ok, ov], j) => { if (j === i) next[k] = v; else next[ok] = ov })
    onChange(next)
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>Pre-fill</label>
      <p style={hint}>
        What the new record opens already holding. Target column on the left, the
        source&apos;s value on the right.
      </p>
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 4 }}>
          <ColumnSelect cols={dstCols} value={k} onChange={(nk) => setRow(i, nk, v)} />
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>=</span>
          <select style={{ ...field, fontSize: '0.8125rem' }} value={v}
            onChange={(e) => setRow(i, k, e.target.value)}>
            <option value={v}>{v || 'Choose a source column…'}</option>
            {srcCols.map((c) => (
              <option key={c.name} value={`{form.${c.name}}`}>
                {c.name} — {c.label}
              </option>
            ))}
          </select>
          <button type="button" style={iconBtn} aria-label="Remove"
            onClick={() => {
              const next = { ...seed }
              delete next[k]
              onChange(next)
            }}>
            <X size={12} />
          </button>
        </div>
      ))}
      <button type="button" style={miniBtn}
        onClick={() => onChange({ ...seed, '': '' })}>
        <Plus size={11} /> Add a pre-filled field
      </button>
    </div>
  )
}

// ── small pieces ───────────────────────────────────────────────────────────

function Head({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10,
      paddingBottom: 8, borderBottom: '1px solid var(--color-border)',
    }}>
      <div style={{ fontSize: '0.8825rem', fontWeight: 700, minWidth: 0, flex: 1 }}>
        {children}
      </div>
      {right}
    </div>
  )
}

function Field({ l, h, children }: { l: string; h?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 11, flex: 1, minWidth: 0 }}>
      <label style={labelStyle}>{l}</label>
      {children}
      {h && <span style={hint}>{h}</span>}
    </div>
  )
}

function Toggle({ on, onChange, label, hint: h }: {
  on: boolean; onChange: (on: boolean) => void; label: string; hint: string
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 7, padding: '8px 9px',
      marginBottom: 11, background: 'var(--color-input-bg, #fff)',
    }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8525rem',
        fontWeight: 600, cursor: 'pointer',
      }}>
        <input type="checkbox" checked={on} style={{ width: 'auto', margin: 0 }}
          onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      <p style={{ ...hint, margin: '4px 0 0' }}>{h}</p>
    </div>
  )
}

function Divider({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.8125rem', letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--color-text-secondary)', fontWeight: 700,
      margin: '16px 0 9px', paddingTop: 9, borderTop: '1px solid var(--color-border)',
    }}>
      {children}
    </div>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--color-text-secondary)',
      background: 'var(--color-bg, #f4f6f9)', border: '1px solid var(--color-border)',
      borderRadius: 7, padding: '8px 9px', margin: '10px 0 0',
    }}>
      {children}
    </p>
  )
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: '0.8125rem', lineHeight: 1.45, color: 'var(--color-warning, #b45309)',
      background: 'var(--color-warning-weak, rgba(180,83,9,0.08))',
      border: '1px solid var(--color-warning, #b45309)', borderRadius: 7,
      padding: '7px 8px', margin: '2px 0 10px',
    }}>
      {children}
    </p>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text)',
}
const hint: React.CSSProperties = {
  fontSize: '0.8125rem', lineHeight: 1.4, color: 'var(--color-text-secondary)', margin: 0,
}
/** Fields sit on the dedicated control ground, never on `--color-bg` — that is
 *  the PAGE ground and in the light theme it is the grey the panel already is. */
const field: React.CSSProperties = {
  width: '100%', minWidth: 0, padding: '0.36rem 0.5rem', borderRadius: 6,
  fontSize: '0.8525rem', border: '1px solid var(--color-border)',
  background: 'var(--color-input-bg, #fff)', color: 'var(--color-text)',
}
const ghost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
  padding: '0.34rem 0.62rem', borderRadius: 6, fontSize: '0.8325rem',
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text)',
}
const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, borderRadius: 5, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text-secondary)', padding: 0,
}
const miniBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer',
  padding: '3px 7px', borderRadius: 5, fontSize: '0.8125rem',
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text)',
}
